import { React, useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Flex, Search, Select, Text, MTable, Tooltip, Button, DeviceStatus, Popover, XCircle } from "@cisco/spaces-dash-lib-styleguide";
import { NodeContext } from "../../contexts/nodeContext";
import * as BusinessHierarchyService from "../../service/business-hierarchy-service";
import { I18nContext } from "../../contexts/i18nContext";
import CameraEmpty from "../../assets/images/location-accesspoints/camera_empty_lh2.svg";
import "../../common/common_style.scss";
import "../../common/global_style.scss";
import "./location-accesspoints.scss";
import CustomSearch from "../custom-search/custom-search";
import useLoader from "../../hooks/useLoader";

function LocationAccessPoints(props) {
  const i18n = useContext(I18nContext);
  const navigate = useNavigate();
  const { node, apCount, locationRowMap } = useContext(NodeContext);
  const [locationNode] = useState({ ...node });
  const [controllersList, setControllersList] = useState([]);
  const [locId, setLocId] = useState(locationNode ? locationNode.id : "");
  const [apListState, setApListState] = useState([]);
  const [apListPaged, setApListPaged] = useState([]);
  const [totalNoOfItems, setTotalNoOfItems] = useState(apCount);
  const rtotalNoOfItems = useRef(apCount);
  const [searchText, setSearchText] = useState("");
  const rsearchText = useRef("");
  const [oldSearchText, setOldSearchText] = useState("");
  const roldSearchText = useRef("");
  const hasWLC = useRef(false);
  const [apListFilteredState, setApListFilteredState] = useState([]);
  const [textColor, setTextColor] = useState("#f7f7f7");
  const [bgColor, setBgColor] = useState({ background: "#656c75" });
  const [showTable, setShowTable] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const rowsPerPageRef = useRef(10);
  const [paginationKey, setPaginationKey] = useState(0);

  const [filterController, setFilterController] = useState(i18n.t("location-access-points.all-text"));
  const { setLoading } = useLoader();
  let apList = [];
  let apListFiltered = [];
  let isWLC = false;
  let selectedController = "";
  const appUserSearchSkeleton = {
    name: "string",
    macAddress: "string"
  };
  const [copyIcon, setCopyIcon] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const hoveredItemId = useRef(null);

  const getLocationPath = (ap) => {
    try {
      const apId = ap.id;
      const apName = ap.name ? ap.name : "";
      const locNameArr = [];
      const parentLoc = locationRowMap[apId];
      if (!parentLoc) {
        return apName;
      }
      const locPathIds = parentLoc.locationPath;
      if (locPathIds && locPathIds.length) {
        locPathIds.forEach((id) => {
          const loc = locationRowMap[id];
          if (loc) {
            locNameArr.push(loc.name);
          }
        });
        const locNameStr = locNameArr.length ? locNameArr.join(" > ") : "";
        return locNameStr;
      }
      if (apName) return apName;
      return "";
    } catch (e) {
      return "";
    }
  };

  const getLocationPathTruncated = (ap) => {
    const apName = ap.name ? ap.name : "";
    const locPath = getLocationPath(ap);
    if (locPath) {
      if (locPath.indexOf(" > ") - 1) {
        const count = locPath.split(" > ").length - 1;
        if (count > 1) {
          const arr = locPath.split(" > ");
          const newLocPathArr = [];
          newLocPathArr.push(arr[0]);
          newLocPathArr.push(arr[arr.length - 1]);
          const newLocPath = newLocPathArr.join(" > ... > ");
          return newLocPath;
        }
        return locPath;
      }
      return locPath;
    }
    return apName;
  };

  const getControllerIp = (accessPoint) => {
    return accessPoint.controllerIP != null ? accessPoint.controllerIP : i18n.t("hierarchy-nodes.na-text");
  };

  const filterWLCrecords = () => {
    const tsearchText = rsearchText.current ? rsearchText.current.toUpperCase() : "";

    apList = apListState || [];
    apListFiltered = apList
      .filter((ap) => {
        if (!ap) return false;

        // Check controller filter
        if (selectedController && selectedController !== i18n.t("location-access-points.all-text") && (!ap.controllerIP || !ap.controllerIP.includes(selectedController))) {
          return false;
        }

        // Check search text filter
        if (tsearchText && (!ap.name || !ap.name.toUpperCase().includes(tsearchText)) && (!ap.macAddress || !ap.macAddress.toUpperCase().includes(tsearchText))) {
          return false;
        }

        return true;
      })
      .map((ap) => ({
        ...ap,
        locationPath: getLocationPath(ap.locationDetails),
        locationPathTruncated: getLocationPathTruncated(ap.locationDetails),
        controllerIp: getControllerIp(ap)
      }));

    setApListFilteredState(apListFiltered);
    setTotalNoOfItems(apListFiltered.length);
    rtotalNoOfItems.current = apListFiltered.length;
  };

  const getStart = (currentPage) => {
    return currentPage * rowsPerPageRef.current;
  };

  const getEnd = (currentPage) => {
    return currentPage * rowsPerPageRef.current + rowsPerPageRef.current;
  };

  const prepareControllerList = () => {
    setControllersList([]);
    const controllersIPS = [];
    const controllersObjs = [];
    if (apList && apList.length > 0) {
      apList.forEach((apObj) => {
        if (apObj.controllerIP && apObj.controllerIP.length > 0)
          apObj.controllerIP.forEach((controllerIPStr) => {
            if (controllerIPStr && controllerIPStr != "" && !controllersIPS.includes(controllerIPStr)) controllersIPS.push(controllerIPStr);
          });
      });
    }
    controllersIPS.unshift(i18n.t("location-access-points.all-text"));
    controllersIPS.forEach((ip) => {
      controllersObjs.push({ label: ip, value: ip });
    });
    setControllersList(controllersObjs);
  };

  const processApListResponseFromSearch = (response) => {
    if (response.status) {
      hasWLC.current = response.data?.hasWLC || false;
      isWLC = response.data?.hasWLC || false;
      let tempApList = response.data?.apList || [];
      apList = tempApList;
      setTotalNoOfItems(apList.length);
      rtotalNoOfItems.current = apList.length;
      setApListState(tempApList);
      if (tempApList && tempApList.length > 0) {
        const updatedApList = tempApList.map((apObj) => {
          const apLocId = apObj.locationDetails.id;
          const apLocName = apObj.locationDetails.name;
          const apLocData = locationRowMap[apLocId];

          // Create a new object with updated properties
          return {
            ...apObj,
            locationDetails: {
              ...apObj.locationDetails,
              name: apLocData && apLocData.parentId && apLocData.parentId === locationNode.id ? `${locationNode.name}/${apLocName}` : `${locationNode.name}/..../${apLocName}`
            }
          };
        });

        // Use updatedApList as needed
        tempApList = updatedApList;
      }
    } else {
      apList = [];
      setApListState([]);
    }
    apListFiltered = apList.map((ap) => ({
      ...ap,
      locationPath: getLocationPath(ap.locationDetails),
      locationPathTruncated: getLocationPathTruncated(ap.locationDetails),
      controllerIp: getControllerIp(ap)
    }));
    setApListFilteredState(apListFiltered);
    // setLoading(false);
  };

  const processApListResponseForInitial = (response) => {
    if (response.status) {
      hasWLC.current = response.data?.hasWLC || false;
      isWLC = response.data?.hasWLC || false;
      let tempApList = response.data?.apList || [];
      apList = tempApList;
      if (apCount <= rowsPerPage && apCount != apList.length) {
        setTotalNoOfItems(apList.length);
        rtotalNoOfItems.current = apList.length;
      }
      setApListState(tempApList);
      if (tempApList && tempApList.length > 0) {
        const updatedApList = tempApList.map((apObj) => {
          const apLocId = apObj.locationDetails.id;
          const apLocName = apObj.locationDetails.name;
          const apLocData = locationRowMap[apLocId];
          return {
            ...apObj,
            locationDetails: {
              ...apObj.locationDetails,
              name: apLocData && apLocData.parentId && apLocData.parentId === locationNode.id ? `${locationNode.name}/${apLocName}` : `${locationNode.name}/..../${apLocName}`
            }
          };
        });
        tempApList = updatedApList;
        console.log(updatedApList);
      }
    } else {
      apList = [];
      setApListState([]);
    }
    apListFiltered = apList.map((ap) => ({
      ...ap,
      locationPath: getLocationPath(ap.locationDetails),
      locationPathTruncated: getLocationPathTruncated(ap.locationDetails),
      controllerIp: getControllerIp(ap)
    }));
    setApListFilteredState(apListFiltered);
    setLoading(false);
  };

  const processApListResponse = (response) => {
    if (response.status) {
      hasWLC.current = response.data?.hasWLC || false;
      isWLC = response.data?.hasWLC || false;
      let tempApList = response.data?.apList || [];
      apList = tempApList;
      setTotalNoOfItems(apCount);
      rtotalNoOfItems.current = apCount;
      setApListState(tempApList);
      if (tempApList && tempApList.length > 0) {
        const updatedApList = tempApList.map((apObj) => {
          const apLocId = apObj.locationDetails.id;
          const apLocName = apObj.locationDetails.name;
          const apLocData = locationRowMap[apLocId];
          return {
            ...apObj,
            locationDetails: {
              ...apObj.locationDetails,
              name: apLocData && apLocData.parentId && apLocData.parentId === locationNode.id ? `${locationNode.name}/${apLocName}` : `${locationNode.name}/..../${apLocName}`
            }
          };
        });
        tempApList = updatedApList;
        console.log(updatedApList);
      }
    } else {
      apList = [];
      setApListState([]);
    }
    apListFiltered = apList.map((ap) => ({
      ...ap,
      locationPath: getLocationPath(ap.locationDetails),
      locationPathTruncated: getLocationPathTruncated(ap.locationDetails),
      controllerIp: getControllerIp(ap)
    }));
    setApListFilteredState(apListFiltered);
    setLoading(false);
  };

  const copyLocationPath = (locationPath) => {
    navigator.clipboard.writeText(locationPath);
    setIsCopied(true);
    setBgColor({
      ...bgColor,
      background: "#FFF"
    });
    setTextColor("#545454");
  };

  const refreshPaginator = () => {
    setPageNumber(0);
    setRowsPerPage(rowsPerPageRef.current);
    // rowsPerPageRef.current = 10;
    setApListPaged(apListFiltered);
  };

  const getAPSearchList = () => {
    setOldSearchText(rsearchText.current);
    roldSearchText.current = rsearchText.current;
    // setLoading(true);
    setShowTable(true);
    BusinessHierarchyService.searchAps(locId, rsearchText.current)
      .then((response) => {
        processApListResponseFromSearch(response);
        refreshPaginator();
      })
      .catch((error) => {
        setLoading(false);
        apList = [];
        setApListState([]);
        apListFiltered = [];
        refreshPaginator();
        console.log(`Location Access Points: Error on getAPSearchList${error}`);
      });
  };

  const getAPList = (currentPageNo = 0) => {
    // setLoading(true);
    setShowTable(true);
    setOldSearchText(rsearchText.current);
    roldSearchText.current = rsearchText.current;
    BusinessHierarchyService.getLocationApList(locId, getStart(currentPageNo), getEnd(currentPageNo))
      .then((response) => {
        const list = response.data?.apList || [];
        if (list.length != apCount) {
          setTotalNoOfItems(list.length);
        }
        processApListResponse(response);
        if (currentPageNo == 0) {
          refreshPaginator();
        } else {
          setApListPaged(apListFiltered);
        }
        if (isWLC) {
          prepareControllerList();
        }
      })
      .catch((error) => {
        setLoading(false);
        apList = [];
        setApListState([]);
        apListFiltered = [];
        refreshPaginator();
        console.log(`Location Access Points: Error on getAPList${error}`);
      });
  };

  const onPageChange = (event) => {
    apListFiltered = apListFilteredState;
    setShowTable(true);
    if (hasWLC.current || (rsearchText.current && rsearchText.current != "")) {
      setApListPaged(apListFiltered);
    } else {
      getAPList(event);
    }
  };

  const clearSearch = () => {
    rsearchText.current = "";
    setSearchText("");
    setPageNumber(0);
    setRowsPerPage(10);
    rowsPerPageRef.current = 10;
    setFilterController(i18n.t("location-access-points.all-text"));
    rtotalNoOfItems.current = apCount;
    setTotalNoOfItems(apCount);
    getAPList(0);
  };

  const onAdminSearchFilter = () => {
    setShowTable(true);
    // if location has WLC, then we have recieved complete list in apList
    if (hasWLC.current) {
      filterWLCrecords();
      refreshPaginator();
    } else {
      // if searchText is not defined, it is first time initialisation and hence dont trigger search
      if (rsearchText.current == undefined || rsearchText.current == roldSearchText.current) return;
      // if location doesn't have WLC, then we have to query API for Aplist
      // if serchText is there send search query else send list query
      if (rsearchText.current != "") {
        rtotalNoOfItems.current = apCount;
        getAPSearchList();
      } else {
        rtotalNoOfItems.current = apCount;
        getAPList(0);
      }
    }
  };

  const initialAPList = (currentPageNo = 0) => {
    rsearchText.current = "";
    setLoading(true);
    setOldSearchText(rsearchText.current);
    setShowTable(true);
    roldSearchText.current = rsearchText.current;
    BusinessHierarchyService.getLocationApList(locId, getStart(currentPageNo), getEnd(currentPageNo))
      .then((response) => {
        setLoading(true);
        if (response && response.data && response.data.apList && response.data.apList.length > 0) {
          setShowTable(true);
        } else {
          setShowTable(false);
        }
        processApListResponseForInitial(response);
        if (currentPageNo == 0) {
          refreshPaginator();
        } else {
          setApListPaged(apListFiltered);
        }
        if (isWLC) {
          prepareControllerList();
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        apList = [];
        setApListState([]);
        apListFiltered = [];
        refreshPaginator();
        console.log(`Location Access Points: Error on getAPList${error}`);
      });
  };

  const getCellValueWithControllerIp = ({ row }) => {
    let controllerIpString = "NA";

    if (row && row.original && row.original.controllerIp) {
      let controllerIp = [];

      if (Array.isArray(row.original.controllerIp)) {
        controllerIp = row.original.controllerIp;
      } else if (row.original.controllerIp === "NA") {
        controllerIp = ["NA"];
      }
      controllerIpString = controllerIp.join(", ");
    }

    return (
      <Text size="p3" weight="regular">
        {controllerIpString}
      </Text>
    );
  };

  const getCellValueWithToolTip = ({ row }) => {
    return (
      <div
        onMouseEnter={(val) => {
          console.log(val.currentTarget.attributes.getNamedItem("id").value);
          hoveredItemId.current = val.currentTarget.attributes.getNamedItem("id").value;
          setCopyIcon(true);
        }}
        onMouseLeave={() => {
          setIsCopied(false);
          setCopyIcon(false);
          setBgColor({
            ...bgColor,
            background: "#656c75"
          });
          setTextColor("#f7f7f7");
        }}
        id={row.id}
      >
        <Flex direction="horizontal" gap={2} align="center" justify="flex-start">
          <Tooltip title={row.original.locationPath} placement="bottom">
            <span className="hover-target">{row.original.locationPathTruncated}</span>
          </Tooltip>
          <Popover
            style={bgColor}
            placement="top"
            kind={isCopied ? "default" : "map"}
            title={
              <Flex direction="horizontal" gap={8} align="center" justify="space-between">
                {isCopied && (
                  <>
                    <DeviceStatus status="good" />
                    <Text color={textColor} size="p4" weight="regular">
                      {i18n.t("hierarchy-base.copied-to-clipboard")}
                    </Text>
                  </>
                )}
                {!isCopied && (
                  <Text color={textColor} size="p4" weight="regular">
                    {i18n.t("hierarchy-base.copy-to-clipboard")}
                  </Text>
                )}
              </Flex>
            }
            visible
          >
            <span className="hover-target">
              <Button
                icon={
                  <Copy
                    size={15}
                    onClick={() => {
                      copyLocationPath(row.original.locationPath);
                    }}
                  />
                }
                kind="tertiary"
                size="sm"
              />
            </span>
          </Popover>
        </Flex>
      </div>
    );
  };

  const filterValue = (event) => {
    setSearchText(event.target.value);
    rsearchText.current = event.target.value;
  };

  const navigateToApConfigure = () => {
    navigate("/setup/wirednetwork");
  };

  const onControllerFilterChange = (value) => {
    if (value) {
      selectedController = value;
      setFilterController(value);
      filterWLCrecords();
      refreshPaginator();
    }
  };

  useEffect(() => {
    console.log("LocationAccessPoints Mount");
    console.log(`${props} ${copyIcon} ${oldSearchText}`);
    setLocId(locationNode ? locationNode.id : "");
    setTotalNoOfItems(apCount);
    if (apCount && apCount > 0) {
      initialAPList();
    }

    return () => {
      console.log("LocationAccessPoints Destroy");
    };
  }, []);

  useEffect(() => {
    setPaginationKey(paginationKey + 1);
  }, [apListPaged]);

  return (
    <div className="ap-wrapper boxShadowBlur" id="access-point-wrapper">
      <Flex direction="vertical" gap={16}>
        <Flex align="center" justify="space-between" gap={8}>
          {/* {!showSearchEmptyState() && apListPaged && apListPaged.length > 0 && (
            <MLink onClick={navigateToApConfigure} size="md">
              {i18n.t("location-cameras.on-board-camera-text")}
            </MLink>
          )} */}
        </Flex>
        {showTable && apListPaged && apListPaged.length >= 0 && (
          <Flex justify="space-between">
            <Flex align="center" gap={16}>
              <Flex gap={8}>
                <Search
                  label=""
                  placeholder={i18n.t("proximity-rule.search-text")}
                  onChange={filterValue}
                  value={searchText}
                  suffix={searchText.length > 0 && <XCircle size={18} onClick={clearSearch} />}
                  options={[]}
                  id="lh20-accesspoint-search"
                />
                <CustomSearch searchSkeleton={appUserSearchSkeleton} customSearchText={searchText} shouldUseGlobalSearch={false} rawData={apListState} onAdminSearchFilter={onAdminSearchFilter} />
                {controllersList && controllersList.length > 0 && (
                  <Select style={{ width: "120px" }} onChange={onControllerFilterChange} options={controllersList} placeholder="Controllers" value={filterController} />
                )}
              </Flex>
            </Flex>
          </Flex>
        )}

        {showTable && apListPaged && apListPaged.length >= 0 && (
          <div className="userTableContainer">
            <MTable.Pagination
              currentPage={pageNumber + 1}
              paginationKey={paginationKey}
              key={apListPaged.length}
              defaultRecordsOption={rowsPerPageRef.current}
              kind={
                hasWLC.current || (rsearchText.current && rsearchText.current != "")
                  ? "client"
                  : {
                      server: {
                        rowCount: totalNoOfItems,
                        onPageChange: (currentPage) => {
                          setPageNumber(currentPage);
                          onPageChange(currentPage);
                        },
                        onRowsPerPageChange: (change) => {
                          rowsPerPageRef.current = change;
                          setRowsPerPage(change);
                          getAPList(0);
                        }
                      }
                    }
              }
              showNavigation="all"
              showOptions
              showTotal
            >
              {controllersList && controllersList.length > 0 ? (
                <MTable
                  columns={[
                    {
                      accessorKey: "name",
                      header: i18n.t("business-location-details.ap-name-text"),
                      meta: {
                        align: "left"
                      },
                      enableSorting: true
                    },
                    {
                      accessorKey: "macAddress",
                      header: i18n.t("business-location-details.mac-address-text"),
                      meta: {
                        align: "left"
                      },
                      enableSorting: true
                    },
                    {
                      accessorKey: "locationPathTruncated",
                      cell: getCellValueWithToolTip,
                      header: i18n.t("location-access-points.location-text"),
                      meta: {
                        align: "left"
                      }
                    },
                    {
                      accessorKey: "controllerIp",
                      cell: getCellValueWithControllerIp,
                      header: i18n.t("business-location-details.controller-ip-text"),
                      meta: {
                        align: "left"
                      }
                    }
                  ]}
                  data={apListPaged}
                />
              ) : (
                <MTable
                  columns={[
                    {
                      accessorKey: "name",
                      header: i18n.t("business-location-details.ap-name-text"),
                      meta: {
                        align: "left"
                      },
                      enableSorting: true
                    },
                    {
                      accessorKey: "macAddress",
                      header: i18n.t("business-location-details.mac-address-text"),
                      meta: {
                        align: "left"
                      },
                      enableSorting: true
                    },
                    {
                      accessorKey: "locationPathTruncated",
                      cell: getCellValueWithToolTip,
                      header: i18n.t("location-access-points.location-text"),
                      meta: {
                        align: "left"
                      }
                    }
                  ]}
                  data={apListPaged}
                />
              )}
            </MTable.Pagination>
          </div>
        )}

        {!showTable && apListPaged && apListPaged.length == 0 && (
          <div className="team-empty-business">
            <div className="divTable empty_wrapper">
              <div className="divTableRow">
                <div className="divTableCell width50_middle">
                  <div className="title">
                    {/* <Heading size="primary">{i18n.t("location-access-points.looks-there-are-no-access-points-for-location-text")}</Heading> */}
                    <Text size="p1" weight="bold">
                      {i18n.t("location-access-points.looks-there-are-no-access-points-for-location-text")}
                    </Text>
                  </div>
                  <div className="desc">
                    <Text size="p2" weight="regular" color="light">
                      {i18n.t("location-access-points.configure-access-point-text")}
                    </Text>
                  </div>
                  <Button type="primary" onClick={navigateToApConfigure}>
                    {i18n.t("location-access-points.configure-access-points")}
                  </Button>
                </div>
                <div className="divTableCell width50_middle">
                  <div>
                    <img alt="AP missing" className="team-empty-img" src={CameraEmpty} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Flex>
    </div>
  );
}

export default LocationAccessPoints;
